import { makeStyles } from '@/styles/theme/tss-react';

import { darkOrLightStyle } from '@/utils/styles';

interface Props {
  isOnDark: boolean | undefined;
}

const useStyles = makeStyles<
  Props,
  't1HoverIcon' | 'linkColumn' | 'linkColumnItem'
>({
  name: 'GlobalFooter',
  uniqId: '1O920P',
})(
  (
    { brandColors, spacing, breakpoints, typography, type },
    props,
    classes
  ) => ({
    container: {
      padding: `${spacing(5)} 0`,
      backgroundColor: darkOrLightStyle(props)(
        brandColors.tintsHeroBlue10,
        brandColors.tintsOffWhite60
      ),
    },
    t1: {
      '&.MuiGrid-root': {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: typography.pxToRem(24),
        [breakpoints.up('sm')]: {
          marginBottom: typography.pxToRem(40),
        },
        [breakpoints.up('md')]: {
          marginBottom: 0,
        },
      },
      '& a': {
        ...type['24_medium'],
        textDecoration: 'none',
      },
    },
    linkColor: {
      color: darkOrLightStyle(props)(
        brandColors.tintsChargeBlue60,
        brandColors.mediumBlue
      ),
      '&:hover': {
        color: darkOrLightStyle(props)(
          brandColors.white,
          brandColors.primaryMain
        ),
      },
    },
    t1Row: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'border-bottom 300ms ease-out',
      borderBottomWidth: typography.pxToRem(2),
      borderBottomStyle: 'solid',
      borderBottomColor: darkOrLightStyle(props)(
        'rgba(255, 255, 255, 0.3)',
        'rgba(0, 0, 0, 0.2)'
      ),
      padding: `${spacing(0)} ${spacing(2)} ${spacing(0)} 0`,
      '&:hover': {
        transition: 'border-bottom 400ms ease-in',
        borderBottomColor: darkOrLightStyle(props)(
          brandColors.white,
          brandColors.primaryMain
        ),
        [`& .${classes.t1HoverIcon}`]: {
          transition: 'opacity 300ms ease-in',
          opacity: 1,
        },
      },
    },
    t1Item: {
      display: 'flex',
      alignItems: 'center',
    },
    t1HoverIcon: {
      transition: 'opacity 300ms ease-out',
      opacity: 0,
      position: 'absolute',
      right: 0,
    },
    externalLinkIcon: {
      marginLeft: spacing(-1),
    },
    firstLinkColumn: {
      [`&.${classes.linkColumn}`]: {
        marginTop: 0,
        [breakpoints.up('sm')]: {
          [`& .${classes.linkColumnItem}, & .linkColumnEyebrow`]: {
            paddingLeft: spacing(1),
          },
        },
      },
    },
    linkColumn: {
      marginTop: spacing(2),
      [`& .${classes.linkColumnItem}, & .linkColumnEyebrow`]: {
        paddingLeft: spacing(1),
      },
      [breakpoints.up('sm')]: {
        marginTop: 0,
        [`& .${classes.linkColumnItem}, & .linkColumnEyebrow`]: {
          paddingLeft: 0,
        },
      },
    },
    linkColumnItem: {
      paddingTop: typography.pxToRem(8),
      paddingBottom: typography.pxToRem(8),
      '& a': {
        ...type['14_medium'],
        whiteSpace: 'nowrap',
      },
    },
    solarByStateExtraSmall: {
      paddingLeft: spacing(1),
    },
    socialIcons: {
      '& a:not(:last-child)': {
        marginRight: spacing(2),
      },
    },
    socialIconsLargeContainer: {
      display: 'none',
      [breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
      },
    },
    lowerContent: {
      marginTop: spacing(2),
      [breakpoints.up('sm')]: {
        marginTop: spacing(3),
      },
    },
    primaryLowerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    stateAndCopy: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      flexWrap: 'wrap',
      [breakpoints.up('sm')]: {
        width: 'auto',
      },
    },
    lowerContentSocials: {
      marginTop: spacing(1),
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      [breakpoints.up('sm')]: {
        width: 'auto',
        marginTop: 0,
      },
      [breakpoints.up('md')]: {
        display: 'none',
      },
    },
    legalLinksSlash: {
      marginLeft: typography.pxToRem(8),
      marginRight: typography.pxToRem(8),
    },
    copyright: {
      color: darkOrLightStyle(props)(
        brandColors.tintsOffWhite40,
        brandColors.tintsNeutral20
      ),
      ...type['14_regular'],
      width: '100%',
      marginTop: spacing(1),
      display: 'flex',
      justifyContent: 'center',
      [breakpoints.up('sm')]: {
        width: 'auto',
        marginTop: 0,
      },
    },
    stateSelector: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: typography.pxToRem(8),
      paddingRight: typography.pxToRem(8),
      [breakpoints.up('sm')]: {
        width: 'auto',
        marginRight: typography.pxToRem(16),
      },
      color: darkOrLightStyle(props)(
        brandColors.tintsChargeBlue60,
        brandColors.mediumBlue
      ),
    },
    legalLinks: {
      color: darkOrLightStyle(props)(brandColors.white, brandColors.black),
      ...type['14_medium'],
      width: '100%',
      marginTop: spacing(1),
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      '& .manageCookieLink': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
      [breakpoints.up('md')]: {
        width: 'auto',
        marginTop: 0,
      },
    },
    legalLink: {
      '&:hover': {
        color: darkOrLightStyle(props)(
          brandColors.tintsOffWhite80,
          brandColors.primaryMain
        ),
      },
    },
    privacyOptions: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: typography.pxToRem(4),
    },
    popularPagesList: {
      '& .be-ix-link-block .be-related-link-container': {
        justifyContent: 'start !important',
        paddingLeft: '0 !important',
      },
    },
  })
);

export default useStyles;
